import AIChatWidget from '@/components/aichat/AiChatWidget';
import DetailsAccordionItem from '@/components/landing/details-accordion-item';
import PageHero from '@/components/landing/page-hero';
import { Button } from '@/components/ui/button';
import LandingLayout from '@/layouts/landing-layout';
import SeoHead from '@/components/seo/SeoHead';
import { Link } from '@inertiajs/react';
import { ArrowRight, CircleAlert, Eye, FileSearch, FolderHeart, Handshake, Scale, ShieldCheck } from 'lucide-react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

const reportingCategories = [
    'Financial Fraud',
    'Bribery',
    'Abuse of Authority',
    'Harassment',
    'Conflict of Interest',
    'Academic Misconduct',
    'Procurement Issues',
    'Data Privacy Violation',
    'Other',
];

const protections = [
    {
        icon: ShieldCheck,
        title: 'Confidentiality',
        description: 'Reports are handled with discretion, and identifying details should only be accessed by authorized personnel.',
    },
    {
        icon: Handshake,
        title: 'Non-Retaliation',
        description: 'Good-faith reporting should not expose students, employees, or stakeholders to retaliation or intimidation.',
    },
    {
        icon: Scale,
        title: 'Fair Investigation',
        description: 'Each concern should be assessed impartially, documented properly, and reviewed using due process.',
    },
];

const steps = [
    {
        title: 'Submit Report',
        description: 'A concern is filed through the public reporting form, with optional supporting evidence.',
    },
    {
        title: 'Initial Review',
        description: 'The report is screened for completeness, urgency, and whether it falls within the system scope.',
    },
    {
        title: 'Investigation',
        description: 'Authorized personnel evaluate facts, review supporting information, and coordinate the appropriate response.',
    },
    {
        title: 'Resolution',
        description: 'A status update is posted to the tracking workflow, and the matter proceeds according to institutional action.',
    },
];

export default function GovernanceIntegrity() {
    return (
        <LandingLayout>
            <SeoHead
                title="Integrity & Whistleblowing System"
                description="Use the Northeastern College Integrity & Whistleblowing System to report concerns and track submitted cases."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section className="bg-muted pb-16">
                <PageHero
                    title="Integrity & Whistleblowing System"
                    description="The Integrity & Whistleblowing System provides a secure and confidential mechanism for reporting suspected misconduct, unethical behavior, fraud, corruption, abuse of authority, and other violations involving the Northeastern College community."
                    breadcrumbs={[
                        { label: 'Home', href: '/' },
                        { label: 'About' },
                        { label: 'Governance', href: '/governance' },
                        { label: 'Integrity & Whistleblowing System' },
                    ]}
                    image="/images/hero/nc1.png"
                    imageAlt="Northeastern College integrity reporting"
                    icon={FolderHeart}
                    actions={
                        <>
                            <Button asChild className="rounded-full bg-[#00963f] text-white hover:bg-[#008538]">
                                <Link href="/governance/integrity/report" prefetch>
                                    Report a Concern
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="rounded-full border-white/25 bg-white/10 text-white hover:bg-white/15 hover:text-white">
                                <Link href="/governance/integrity/track" prefetch>
                                    Track Report
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="rounded-full border-white/25 bg-white/10 text-white hover:bg-white/15 hover:text-white">
                                <Link href="#how-it-works">How It Works</Link>
                            </Button>
                        </>
                    }
                />

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl space-y-8 px-4">
                    <section id="how-it-works" className="bg-card border-border rounded-2xl border p-6 shadow-sm md:p-8">
                        <div className="mb-6 max-w-3xl">
                            <div className="mb-3 inline-flex rounded-full bg-[#0A7A35]/10 px-3 py-1 text-xs font-bold tracking-wider text-[#0A7A35] uppercase">
                                How It Works
                            </div>
                            <h2 className="text-foreground text-2xl font-bold md:text-3xl">A clear reporting and review flow</h2>
                            <p className="text-muted-foreground mt-3 text-sm leading-relaxed md:text-base">
                                The process below shows the public-facing stages of the reporting workflow. Internal notes and confidential case
                                details are not exposed on the tracking page.
                            </p>
                        </div>

                        <div className="grid gap-4 md:grid-cols-4">
                            {steps.map((step, index) => (
                                <div key={step.title} className="relative">
                                    <div className="bg-muted/40 border-border flex h-full flex-col rounded-2xl border p-5">
                                        <div className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#00963f] text-sm font-bold text-white">
                                            {index + 1}
                                        </div>
                                        <h3 className="text-foreground text-base font-bold">{step.title}</h3>
                                        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{step.description}</p>
                                    </div>
                                    {index < steps.length - 1 ? (
                                        <div className="pointer-events-none absolute top-1/2 -right-3 hidden -translate-y-1/2 text-[#0A7A35] md:block">
                                            <ArrowRight className="h-5 w-5" aria-hidden="true" />
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="grid gap-8 xl:grid-cols-[1.2fr_0.8fr]">
                        <div className="bg-card border-border rounded-2xl border p-6 shadow-sm md:p-8">
                            <div className="mb-6 max-w-2xl">
                                <div className="mb-3 inline-flex rounded-full bg-[#0A7A35]/10 px-3 py-1 text-xs font-bold tracking-wider text-[#0A7A35] uppercase">
                                    Reporting Categories
                                </div>
                                <h2 className="text-foreground text-2xl font-bold md:text-3xl">Common concern types</h2>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                {reportingCategories.map((category) => (
                                    <span
                                        key={category}
                                        className="inline-flex items-center rounded-full border border-[#0A7A35]/15 bg-[#0A7A35]/8 px-4 py-2 text-sm font-semibold text-[#0A7A35] dark:text-[#21d36b]"
                                    >
                                        {category}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="bg-card border-border rounded-2xl border p-6 shadow-sm md:p-8">
                            <div className="mb-6 inline-flex rounded-full bg-amber-500/10 px-3 py-1 text-xs font-bold tracking-wider text-amber-700 uppercase dark:text-amber-300">
                                Public Access
                            </div>
                            <h2 className="text-foreground text-2xl font-bold">No login required</h2>
                            <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                                This reporting flow is publicly accessible so concerns can be submitted without account creation. Tracking is handled
                                using the issued tracking number and secret PIN.
                            </p>
                            <div className="bg-muted/40 border-border mt-5 rounded-2xl border p-4">
                                <div className="flex items-start gap-3">
                                    <CircleAlert className="mt-0.5 h-5 w-5 text-[#0A7A35]" aria-hidden="true" />
                                    <p className="text-muted-foreground text-sm leading-relaxed">
                                        Sensitive internal notes remain private. The public tracking view only shows the current status, the latest
                                        update date, and any investigator message intended for the reporter.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="grid gap-6 md:grid-cols-3">
                        {protections.map((item) => (
                            <div key={item.title} className="bg-card border-border rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md">
                                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0A7A35]/10 text-[#0A7A35] dark:text-[#21d36b]">
                                    <item.icon className="h-6 w-6" aria-hidden="true" />
                                </div>
                                <h3 className="text-foreground text-lg font-bold">{item.title}</h3>
                                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </section>

                    <section className="bg-card border-border rounded-2xl border p-6 shadow-sm md:p-8">
                        <div className="mb-6 max-w-3xl">
                            <div className="mb-3 inline-flex rounded-full bg-[#0A7A35]/10 px-3 py-1 text-xs font-bold tracking-wider text-[#0A7A35] uppercase">
                                Frequently Asked Questions
                            </div>
                            <h2 className="text-foreground text-2xl font-bold md:text-3xl">Reporting support</h2>
                        </div>

                        <div className="space-y-4">
                            <DetailsAccordionItem title="Can I report anonymously?">
                                Yes. The reporting form allows anonymous submission. If you choose that option, your name field is hidden and not
                                required.
                            </DetailsAccordionItem>
                            <DetailsAccordionItem title="Will my identity be protected?">
                                Identifying details should only be handled by authorized personnel and only when needed to evaluate the concern.
                            </DetailsAccordionItem>
                            <DetailsAccordionItem title="How do I track my report?">
                                After submission, you will receive a tracking number and a secret PIN. Use both on the tracking page to see public
                                case updates.
                            </DetailsAccordionItem>
                            <DetailsAccordionItem title="What happens after submission?">
                                Reports move through an initial review, possible investigation, and resolution workflow. The tracking page only shows
                                public-facing status information and investigator messages meant for the reporter.
                            </DetailsAccordionItem>
                        </div>
                    </section>

                    <section className="grid gap-4 md:grid-cols-2">
                        <Button asChild className="h-auto rounded-2xl bg-[#00963f] px-6 py-5 text-left text-white hover:bg-[#008538]">
                            <Link href="/governance/integrity/report" prefetch className="flex items-center justify-between">
                                <span>
                                    <span className="block text-lg font-bold">Report a Concern</span>
                                    <span className="mt-1 block text-sm text-white/85">Open the public submission form and generate a secure tracking reference.</span>
                                </span>
                                <FileSearch className="h-5 w-5" aria-hidden="true" />
                            </Link>
                        </Button>
                        <Button asChild variant="outline" className="h-auto rounded-2xl px-6 py-5 text-left">
                            <Link href="/governance/integrity/track" prefetch className="flex items-center justify-between">
                                <span>
                                    <span className="block text-lg font-bold">Track a Submitted Report</span>
                                    <span className="mt-1 block text-sm text-muted-foreground">Check the current status and latest investigator message using your credentials.</span>
                                </span>
                                <Eye className="h-5 w-5" aria-hidden="true" />
                            </Link>
                        </Button>
                    </section>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
